import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthProvider, useAuth } from './context/AuthContext';
import InvitationResponse from './components/InvitationResponse';
import NavBar from './components/NavBar';
import { SnackbarProvider } from 'notistack';

// Import pages (we'll create these next)
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CreateEvent from './pages/CreateEvent';
import EditEvent from './pages/EditEvent';
import EventDetails from './pages/EventDetails';
import PublicVote from './pages/PublicVote';
import Landing from './pages/Landing';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import Availability from './pages/Availability';
import BookTime from './pages/BookTime';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import CreateQuickMeet from './pages/CreateQuickMeet';
import QuickMeetDetail from './pages/QuickMeetDetail';
import QuickMeetPublic from './pages/QuickMeetPublic';

const theme = createTheme({
  palette: {
    primary: {
      main: '#6C5CE7',
      dark: '#5848C2',
      light: '#A29BFE',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#A29BFE',
      contrastText: '#0F172A',
    },
    success: {
      main: '#22C55E',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#475569',
      disabled: '#94A3B8',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: 'linear-gradient(135deg, #6C5CE7, #A29BFE)',
          color: '#FFFFFF',
          fontWeight: 'bold',
          '&:hover': {
            background: 'linear-gradient(135deg, #5848C2, #6C5CE7)',
            color: '#FFFFFF',
          },
          '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.5)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#EDE9FE',
          color: '#6C5CE7',
        },
      },
    },
  },
});

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  if (!token) {
    return <Navigate to="/login" />;
  }
  return children;
};

// Root route — send logged-in users straight to dashboard
const RootRoute = () => {
  const { token } = useAuth();
  return token ? <Navigate to="/dashboard" replace /> : <Landing />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <AuthProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Router>
              <NavBar />
              <Routes>
                <Route path="/" element={<RootRoute />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/create-event"
                  element={
                    <ProtectedRoute>
                      <CreateEvent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events/:id"
                  element={
                    <ProtectedRoute>
                      <EventDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events/:id/edit"
                  element={
                    <ProtectedRoute>
                      <EditEvent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/availability"
                  element={
                    <ProtectedRoute>
                      <Availability />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/create-quick-meet"
                  element={
                    <ProtectedRoute>
                      <CreateQuickMeet />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quick-meets/:id"
                  element={
                    <ProtectedRoute>
                      <QuickMeetDetail />
                    </ProtectedRoute>
                  }
                />
                <Route path="/invitations/:token" element={<InvitationResponse />} />
                <Route path="/vote/:token" element={<PublicVote />} />
                <Route path="/book/:username" element={<BookTime />} />
                <Route path="/meet/:token" element={<QuickMeetPublic />} />
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              </Routes>
            </Router>
          </LocalizationProvider>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
